import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getStoredConsent, setStoredConsent, loadAnalytics } from "../lib/analytics";
import { buildLocalizedPath } from "../constants/languages";

// "checking" avoids a flash of the banner on every navigation before we've
// read localStorage; "pending" means a choice is needed right now.
const CookieConsentBanner = () => {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored === "accepted") {
      loadAnalytics();
      setStatus("resolved");
    } else if (stored === "declined") {
      setStatus("resolved");
    } else {
      setStatus("pending");
    }

    // The footer's "Cookie Settings" link dispatches this so a visitor can
    // change their mind later, per the Privacy Policy's own promise that
    // consent can be withdrawn or changed at any time.
    const reopen = () => setStatus("pending");
    window.addEventListener("ma-reopen-cookie-settings", reopen);
    return () => window.removeEventListener("ma-reopen-cookie-settings", reopen);
  }, []);

  if (status !== "pending") return null;

  const handleAccept = () => {
    setStoredConsent("accepted");
    loadAnalytics();
    setStatus("resolved");
  };

  const handleDecline = () => {
    setStoredConsent("declined");
    setStatus("resolved");
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] bg-navy text-white border-t border-gold/30 shadow-2xl"
      data-testid="cookie-consent-banner"
    >
      <div className="container-custom py-5 flex flex-col sm:flex-row items-center gap-4">
        <p className="text-sm text-slate-300 leading-relaxed flex-1">
          {t("cookieBanner.message")}{" "}
          <Link
            to={buildLocalizedPath("/privacy", i18n.language)}
            className="text-gold underline hover:text-gold-light"
          >
            {t("cookieBanner.privacyLink")}
          </Link>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {/* min-h-[44px] guarantees a real touch target on mobile even
              though the padding/font below is sized for a compact, low-key
              look — this is a small utility banner, not a primary CTA. */}
          <button
            onClick={handleDecline}
            data-testid="cookie-decline-button"
            className="min-h-[44px] px-4 inline-flex items-center justify-center rounded text-xs font-semibold border border-slate-500 text-white hover:bg-slate-800 transition-colors"
          >
            {t("cookieBanner.decline")}
          </button>
          <button
            onClick={handleAccept}
            data-testid="cookie-accept-button"
            className="min-h-[44px] px-4 inline-flex items-center justify-center rounded text-xs font-semibold bg-gold text-navy hover:bg-gold-light transition-colors"
          >
            {t("cookieBanner.accept")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
